import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const fmtCurr = (v) => v == null ? "—" : "$" + v.toLocaleString();
const fmtHrs = (h) => h == null ? "—" : h.toLocaleString() + "h";

export default function PerformanceTables({ loading, tab, setTab, artists, programs, canCost, onArtistClick, nav }) {
  return (
    <Tabs value={tab} onValueChange={setTab}>
      <TabsList>
        <TabsTrigger value="artists" data-testid="tab-artists">By Artist</TabsTrigger>
        <TabsTrigger value="programs" data-testid="tab-programs">By Program</TabsTrigger>
      </TabsList>
      <TabsContent value="artists" className="mt-4">
        <ArtistsTable loading={loading} data={artists} canCost={canCost} onClick={onArtistClick} nav={nav} />
      </TabsContent>
      <TabsContent value="programs" className="mt-4">
        <ProgramsTable loading={loading} data={programs} canCost={canCost} nav={nav} />
      </TabsContent>
    </Tabs>
  );
}

function ArtistsTable({ loading, data, canCost, onClick, nav }) {
  const cols = canCost ? 6 : 5;
  
  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Artist</TableHead>
              <TableHead className="text-right">Hours</TableHead>
              {canCost && <TableHead className="text-right">Cost</TableHead>}
              <TableHead className="text-right">Programs</TableHead>
              <TableHead>Top Program</TableHead>
              <TableHead>Flags</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={cols} className="text-center py-8">
                  <div className="animate-spin w-5 h-5 border-2 border-primary border-t-transparent rounded-full mx-auto" />
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={cols} className="text-center py-8 text-muted-foreground">No data</TableCell>
              </TableRow>
            ) : (
              data.map(a => (
                <TableRow key={a.artist_id} className="cursor-pointer hover:bg-muted/50" onClick={() => onClick(a.artist_id)} data-testid={"artist-row-" + a.artist_id}>
                  <TableCell>
                    <span className="font-medium">{a.artist_name || "?"}</span>
                    {a.status !== "Active" && <Badge variant="outline" className="ml-2 text-xs">{a.status}</Badge>}
                  </TableCell>
                  <TableCell className="text-right font-mono">{fmtHrs(a.allocated_hours)}</TableCell>
                  {canCost && <TableCell className="text-right font-mono">{fmtCurr(a.allocated_cost)}</TableCell>}
                  <TableCell className="text-right">{a.programs_count}</TableCell>
                  <TableCell>
                    {a.top_program ? (
                      <Button variant="link" size="sm" className="h-auto p-0" onClick={(e) => { e.stopPropagation(); nav("/programs/" + a.top_program.id); }}>
                        {a.top_program.name}
                      </Button>
                    ) : "—"}
                  </TableCell>
                  <TableCell>
                    {a.flags && a.flags.includes("missing_rate") && <Badge variant="outline" className="text-amber-500 border-amber-500/30 text-xs">No Rate</Badge>}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function ProgramsTable({ loading, data, canCost, nav }) {
  const cols = canCost ? 5 : 3;
  
  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Program</TableHead>
              <TableHead className="text-right">Hours</TableHead>
              {canCost && <TableHead className="text-right">Cost</TableHead>}
              <TableHead>Artists</TableHead>
              {canCost && <TableHead className="text-right">Top %</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={cols} className="text-center py-8">
                  <div className="animate-spin w-5 h-5 border-2 border-primary border-t-transparent rounded-full mx-auto" />
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={cols} className="text-center py-8 text-muted-foreground">No data</TableCell>
              </TableRow>
            ) : (
              data.map(p => (
                <TableRow key={p.program_id} className="cursor-pointer hover:bg-muted/50" onClick={() => nav("/programs/" + p.program_id)} data-testid={"program-row-" + p.program_id}>
                  <TableCell><span className="font-medium">{p.program_name}</span></TableCell>
                  <TableCell className="text-right font-mono">{fmtHrs(p.allocated_hours)}</TableCell>
                  {canCost && <TableCell className="text-right font-mono">{fmtCurr(p.allocated_cost)}</TableCell>}
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {p.artists.slice(0, 3).map(a => <Badge key={a.id} variant="outline" className="text-xs">{a.name}</Badge>)}
                      {p.artists.length > 3 && <Badge variant="outline" className="text-xs">+{p.artists.length - 3}</Badge>}
                    </div>
                  </TableCell>
                  {canCost && <TableCell className="text-right">{p.top_artist_concentration ? p.top_artist_concentration + "%" : "—"}</TableCell>}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
